import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, Loader2, Users, ChevronDown, ChevronRight, Zap, BarChart3, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { productsApi, copilotApi, unifiedCopilotApi, personasApi, epicsApi, featuresApi, goalsApi, stripeApi } from '../services/api';
import EditModal from '../components/product/EditModal';
import FeatureLimitModal from '../components/product/FeatureLimitModal';
import UpgradeContextModal from '../components/billing/UpgradeContextModal';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { getOperationState, clearOperationState, saveOperationState } from '../hooks/useOperationState';
import { API_BASE_URL } from '../config/api';

const NewProductPage = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [productName, setProductName] = useState('');
  const [productVision, setProductVision] = useState('');
  const [aiInstructions, setAiInstructions] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // Step 2: AI Interpretation fields
  const [extractedName, setExtractedName] = useState('');
  const [extractedVision, setExtractedVision] = useState('');
  const [extractedAudience, setExtractedAudience] = useState([]); // Array of audience segments
  const [extractedEpics, setExtractedEpics] = useState([]); // Array of epics (was features)
  const [extractedGoals, setExtractedGoals] = useState([]); // Array of goals (was metrics)
  
  // Expanded sections
  const [expandedEpics, setExpandedEpics] = useState(new Set());
  
  // Feature limit modal
  const [showFeatureLimitModal, setShowFeatureLimitModal] = useState(false);
  const [featureUsage, setFeatureUsage] = useState(null);
  const [totalNewFeatures, setTotalNewFeatures] = useState(0);
  const [expandedGoals, setExpandedGoals] = useState(new Set());
  
  // Product limit modal - now using UpgradeContextModal
  const [showProductLimitModal, setShowProductLimitModal] = useState(false);
  const [productUsage, setProductUsage] = useState(null);
  const [currentLimitType, setCurrentLimitType] = useState('products');
  
  // Generated personas
  const [generatedPersonas, setGeneratedPersonas] = useState([]);
  const [expandedPersonas, setExpandedPersonas] = useState(new Set());
  
  // Product-level segment tags (for autocomplete suggestions)
  const [productTags, setProductTags] = useState(new Set());
  
  // Edit Modal State
  const [editModal, setEditModal] = useState({
    isOpen: false,
    type: null, // 'vision', 'epic', 'persona', 'goal'
    index: null, // null for new item, number for editing existing
    data: null
  });
  const [editFormData, setEditFormData] = useState({});
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [copilotInput, setCopilotInput] = useState('');
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const chatEndRef = useRef(null);
  
  // Processing state
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [processingStartTime, setProcessingStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [processingSteps, setProcessingSteps] = useState([]); // Steps frozen at start of processing

  // Get current wizard state for saving
  const getWizardState = useCallback(() => ({
    step,
    productName,
    productVision,
    extractedName,
    extractedVision,
    extractedAudience,
    extractedEpics,
    extractedGoals,
    generatedPersonas,
  }), [step, productName, productVision, extractedName, extractedVision, extractedAudience, extractedEpics, extractedGoals, generatedPersonas]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const resume = searchParams.get('resume');
    
    if (checkout || resume) {
      // Process checkout return
      const processReturn = async () => {
        const savedState = getOperationState();
        
        if (checkout === 'success') {
          // Sync subscription to ensure we have latest status
          try {
            await stripeApi.syncSubscription();
          } catch (error) {
            console.warn('Failed to sync subscription:', error);
          }
          
          toast.success('Upgrade successful! 🎉', {
            description: 'Your plan has been upgraded. You can now continue creating your product.',
            duration: 5000,
          });
          
          // Restore saved state if available
          if (savedState && savedState.state) {
            const state = savedState.state;
            setStep(state.step || 2);
            setProductName(state.productName || '');
            setProductVision(state.productVision || '');
            setExtractedName(state.extractedName || '');
            setExtractedVision(state.extractedVision || '');
            setExtractedAudience(state.extractedAudience || []);
            setExtractedEpics(state.extractedEpics || []);
            setExtractedGoals(state.extractedGoals || []);
            setGeneratedPersonas(state.generatedPersonas || []);
          }
          
          clearOperationState();
        } else if (checkout === 'cancelled') {
          toast.info('Checkout cancelled', {
            description: 'No charges were made. You can try again anytime.',
          });
          
          // Still restore state so user can continue
          if (savedState && savedState.state) {
            const state = savedState.state;
            setStep(state.step || 2);
            setProductName(state.productName || '');
            setProductVision(state.productVision || '');
            setExtractedName(state.extractedName || '');
            setExtractedVision(state.extractedVision || '');
            setExtractedAudience(state.extractedAudience || []);
            setExtractedEpics(state.extractedEpics || []);
            setExtractedGoals(state.extractedGoals || []);
            setGeneratedPersonas(state.generatedPersonas || []);
          }
        }
        
        // Clean up URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('checkout');
        newParams.delete('resume');
        setSearchParams(newParams, { replace: true });
      };
      
      processReturn();
    }
  }, [searchParams, setSearchParams]);

  // Restore wizard state from session (legacy support)
  useEffect(() => {
    const savedState = sessionStorage.getItem('wizardState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        setStep(state.step || 2);
        setProductName(state.productName || '');
        setProductVision(state.productVision || '');
        setExtractedName(state.extractedName || '');
        setExtractedVision(state.extractedVision || '');
        setExtractedAudience(state.extractedAudience || []);
        setExtractedEpics(state.extractedEpics || []);
        setExtractedGoals(state.extractedGoals || []);
        setGeneratedPersonas(state.generatedPersonas || []);
        toast.success('Welcome back!', { description: 'Your product wizard progress has been restored.' });
        sessionStorage.removeItem('wizardState');
      } catch (e) {
        console.error('Error restoring wizard state:', e);
      }
    }
  }, []);

  // Fetch feature usage on mount
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const response = await featuresApi.getUsage();
        setFeatureUsage(response.data);
      } catch (error) {
        console.error('Error fetching feature usage:', error);
      }
    };
    fetchUsage();
  }, []);
  
  // Build processing steps based on current sources
  const buildProcessingSteps = (hasUrls, hasFiles) => {
    const steps = [];
    if (hasUrls) {
      steps.push({ label: 'Crawling website...', key: 'crawl', dynamic: true });
    }
    if (hasFiles) {
      steps.push({ label: 'Extracting from documents...', key: 'extract' });
    }
    steps.push(
      { label: 'Analyzing product vision...', key: 'analyze' },
      { label: 'Extracting features & goals...', key: 'features' },
      { label: 'Generating personas...', key: 'personas' },
      { label: 'Finalizing results...', key: 'finalize' }
    );
    return steps;
  };
  
  const [showPersonaGeneration, setShowPersonaGeneration] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveStep, setSaveStep] = useState(0); // Track save progress
  const [processingAI, setProcessingAI] = useState(false);
  
  // Sources state (combined files + URLs)
  const [sourceUrls, setSourceUrls] = useState([]); // Array of URLs to crawl
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInputValue, setUrlInputValue] = useState('');
  const [showAdvancedCrawl, setShowAdvancedCrawl] = useState(false);
  const [crawlMode, setCrawlMode] = useState('crawl'); // 'single' or 'crawl'
  
  // Processing modal state
  const [crawlProgress, setCrawlProgress] = useState(null); // Real-time crawl status

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(prev => [...prev, ...files]);
    toast.success(`${files.length} file(s) uploaded`);
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // URL Source management
  const handleAddUrl = () => {
    if (!urlInputValue.trim()) return;
    
    let url = urlInputValue.trim();
    // Add https if no protocol
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    // Check for duplicates
    if (sourceUrls.includes(url)) {
      toast.error('This URL is already added');
      return;
    }
    
    setSourceUrls(prev => [...prev, url]);
    setUrlInputValue('');
    setShowUrlInput(false);
    toast.success('Website added to sources');
  };
  
  const handleRemoveUrl = (index) => {
    setSourceUrls(prev => prev.filter((_, i) => i !== index));
  };
  
  // Crawl a URL and return content (used during processing)
  const crawlUrl = async (url) => {
    const response = await fetch(`${API_BASE_URL}/api/copilot/extract-from-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        url: url,
        instructions: aiInstructions || null,
        single_page: crawlMode === 'single',
        product_name: productName || null
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'progress') {
              setCrawlProgress({
                status: data.status,
                message: data.message,
                currentUrl: data.current_url,
                pagesFound: data.pages_found || 0,
                pagesFetched: data.pages_fetched || 0,
                totalChars: data.total_chars || 0
              });
            } else if (data.type === 'complete') {
              result = data;
            } else if (data.type === 'error') {
              throw new Error(data.error);
            }
          } catch (e) {
            if (e.message) throw e;
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    }
    
    return result;
  };

  // Toggle section expansions
  const toggleExpand = (setter, currentSet, index) => {
    const newExpanded = new Set(currentSet);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setter(newExpanded);
  };

  const toggleEpicExpand = (index) => toggleExpand(setExpandedEpics, expandedEpics, index);
  const toggleGoalExpand = (index) => toggleExpand(setExpandedGoals, expandedGoals, index);
  const togglePersonaExpand = (index) => toggleExpand(setExpandedPersonas, expandedPersonas, index);

  // Step 1: Process with AI -> Go to Step 2
  const handleProcessWithAI = async () => {
    // Check if user has a pending URL that hasn't been added
    if (urlInputValue.trim()) {
      toast.error('You have a URL that hasn\'t been added. Click "Add" to include it, or "Cancel" to remove it.');
      return;
    }
    
    const finalSourceUrls = [...sourceUrls];
    
    if (!productVision.trim() && uploadedFiles.length === 0 && finalSourceUrls.length === 0) {
      toast.error('Please provide product vision (text, document, or website URL)');
      return;
    }

    // Build and freeze the processing steps before showing modal
    const steps = buildProcessingSteps(finalSourceUrls.length > 0, uploadedFiles.length > 0);
    setProcessingSteps(steps);
    
    setProcessingAI(true);
    setShowProcessingModal(true);
    setProcessingStep(0);
    setProcessingStartTime(Date.now());
    setElapsedTime(0);
    setCrawlProgress(null);
    
    // Update elapsed time every second - defined outside try so catch can access it
    let elapsedInterval = null;
    
    try {
      elapsedInterval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      let crawledContent = '';
      let crawlExtractedData = null;
      
      // Step 1: Crawl URLs if any
      if (finalSourceUrls.length > 0) {
        setProcessingStep(0); // Crawling step
        for (const url of finalSourceUrls) {
          try {
            const crawlResult = await crawlUrl(url);
            console.log('[DEBUG CRAWL] crawlResult:', JSON.stringify(crawlResult, null, 2));
            if (crawlResult && crawlResult.data) {
              // Store the extracted data from crawl
              crawlExtractedData = crawlResult.data;
              console.log('[DEBUG CRAWL] crawlExtractedData:', JSON.stringify(crawlExtractedData, null, 2));
              console.log('[DEBUG CRAWL] extracted_features:', crawlExtractedData.extracted_features);
              console.log('[DEBUG CRAWL] extracted_metrics:', crawlExtractedData.extracted_metrics);
              // Use raw_content (actual crawled text) when combining with documents
              // This ensures document + URL uploads get full context from both sources
              if (crawlResult.raw_content) {
                crawledContent += `\n\n=== Content from ${url} ===\n`;
                crawledContent += crawlResult.raw_content;
              } else {
                // Fallback: just note that content was crawled (for backward compatibility)
                crawledContent += `\n\n=== Content from ${url} ===\n`;
                crawledContent += `(Crawled ${crawlResult.crawl_info?.pages_crawled?.length || 1} pages)`;
              }
            }
          } catch (crawlError) {
            console.error(`Failed to crawl ${url}:`, crawlError);
            toast.error(`Failed to crawl ${url}`);
          }
        }
        setCrawlProgress(null);
      }
      
      // Move to next step
      const crawlStepOffset = finalSourceUrls.length > 0 ? 1 : 0;
      setProcessingStep(crawlStepOffset);
      
      let response;
      
      // Combine vision text with any crawled content notes
      const combinedVision = [productVision, crawledContent].filter(Boolean).join('\n\n');
      
      // If we have files, use the file upload endpoint
      if (uploadedFiles.length > 0) {
        const formData = new FormData();
        formData.append('vision_text', combinedVision || '');
        if (productName) formData.append('product_name', productName);
        if (aiInstructions) formData.append('instructions', aiInstructions);
        
        // Add all files
        uploadedFiles.forEach(file => {
          formData.append('files', file);
        });
        
        response = await copilotApi.extractProductWithFiles(formData);
      } else if (crawlExtractedData) {
        // We have crawled + extracted data - USE IT! Don't re-extract.
        // This handles both URL-only AND URL+text cases where crawl succeeded
        console.log('[DEBUG] Using crawlExtractedData instead of re-extracting');
        response = { data: crawlExtractedData };
      } else if (finalSourceUrls.length > 0 && !crawlExtractedData) {
        // URL was provided but crawl failed - stay on Step 1 and let user know
        if (elapsedInterval) clearInterval(elapsedInterval);
        setShowProcessingModal(false);
        setProcessingAI(false);
        toast.error('Could not extract content from the website. Please try a different URL or add a text description.');
        return; // Stay on Step 1
      } else {
        // Text only (no URLs, no files), use the simple endpoint
        response = await copilotApi.extractProduct({
          vision_text: combinedVision || '',
          product_name: productName || null,
          instructions: aiInstructions || null,
        });
      }

      // Quickly complete any remaining steps with a brief delay between each
      const completeRemainingSteps = async () => {
        for (let i = crawlStepOffset + 1; i < steps.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 400));
          setProcessingStep(i);
        }
        await new Promise(resolve => setTimeout(resolve, 600));
      };
      
      await completeRemainingSteps();
      
      console.log('[DEBUG] Full response object:', response);
      console.log('[DEBUG] Response.data:', response?.data);
      
      // Handle both axios response format and manual format
      const extracted = response?.data || response;
      
      console.log('[DEBUG] Extracted data:', extracted);
      
      if (!extracted || !extracted.name) {
        console.error('[DEBUG] Invalid extracted data:', extracted);
        throw new Error('No valid data returned from extraction API');
      }
      
      // Parse audience into structured array
      const parseAudience = (audience) => {
        if (!audience) return [];
        
        // If it's already an array, process each item
        if (Array.isArray(audience)) {
          return audience.map((a, i) => {
            if (typeof a === 'string') {
              return { segment_name: a, description: '', type: 'Primary' };
            }
            return {
              segment_name: a.segment_name || a.name || `Audience ${i + 1}`,
              description: a.description || '',
              attributes: a.attributes || [],
              primary_use_cases: a.primary_use_cases || a.use_cases || [],
              demographics: a.demographics,
              psychographics: a.psychographics,
              type: a.type || 'Primary'
            };
          });
        }
        
        // Handle object with primary_users/secondary_users structure
        if (typeof audience === 'object') {
          const segments = [];
          
          // Handle primary_users array
          if (audience.primary_users && Array.isArray(audience.primary_users)) {
            audience.primary_users.forEach(user => {
              segments.push({
                segment_name: user.segment_name || user.name || 'Primary User',
                description: user.description || '',
                attributes: user.attributes || [],
                primary_use_cases: user.primary_use_cases || user.use_cases || [],
                type: 'Primary'
              });
            });
          }
          
          // Handle secondary_users array
          if (audience.secondary_users && Array.isArray(audience.secondary_users)) {
            audience.secondary_users.forEach(user => {
              segments.push({
                segment_name: user.segment_name || user.name || 'Secondary User',
                description: user.description || '',
                attributes: user.attributes || [],
                primary_use_cases: user.primary_use_cases || user.use_cases || [],
                type: 'Partner'
              });
            });
          }
          
          // Handle other nested structures (legacy format)
          if (segments.length === 0) {
            Object.keys(audience).forEach(key => {
              const items = audience[key];
              if (Array.isArray(items)) {
                const type = key.includes('secondary') || key.includes('partner') ? 'Partner' : 'Primary';
                items.forEach(a => {
                  if (typeof a === 'object') {
                    segments.push({
                      segment_name: a.segment_name || a.name || key,
                      description: a.description || '',
                      attributes: a.attributes || [],
                      primary_use_cases: a.primary_use_cases || [],
                      type
                    });
                  }
                });
              } else if (typeof items === 'object' && items.segment_name) {
                segments.push({
                  ...items,
                  type: key.includes('secondary') ? 'Partner' : 'Primary'
                });
              }
            });
          }
          
          if (segments.length > 0) return segments;
          
          // If it's a single object with segment_name, wrap it
          if (audience.segment_name) {
            return [{ ...audience, type: 'Primary' }];
          }
        }
        
        // Fallback for string
        if (typeof audience === 'string') {
          return [{ segment_name: 'Target Audience', description: audience, type: 'Primary' }];
        }
        
        return [];
      };

      // Parse features into structured array - normalize to use 'requirements' field
      const parseEpics = (features) => {
        console.log('[parseEpics] Input type:', typeof features, 'value:', features);
        if (!features) return [];
        if (Array.isArray(features)) {
          const result = features.map((f, i) => {
            console.log('[parseEpics] Processing item', i, ':', JSON.stringify(f));
            if (typeof f === 'string') return { name: `Feature ${i + 1}`, description: f, requirements: [] };
            
            // Extract capabilities from various possible locations - be very thorough
            let caps = [];
            if (Array.isArray(f.capabilities)) caps = f.capabilities;
            else if (Array.isArray(f.requirements)) caps = f.requirements;
            else if (Array.isArray(f.features)) caps = f.features;
            else if (Array.isArray(f.items)) caps = f.items;
            else if (Array.isArray(f.sub_features)) caps = f.sub_features;
            else if (Array.isArray(f.children)) caps = f.children;
            else if (f.capabilities && typeof f.capabilities === 'string') caps = [f.capabilities];
            else if (f.description && !f.capabilities && !f.requirements) {
              // If no capabilities but has description, use description as single capability
              // This handles cases where AI returns just {name, description} without capabilities
            }
            
            // Also check for nested objects that might contain strings
            if (caps.length > 0 && typeof caps[0] === 'object') {
              // If caps contains objects, extract their names/descriptions
              caps = caps.map(c => {
                if (typeof c === 'string') return c;
                return c.name || c.title || c.description || JSON.stringify(c);
              });
            }
            
            // Handle rich feature objects - normalize capabilities to requirements
            const epic = { 
              name: f.name || f.feature || f.title || f.epic_name || `Feature ${i + 1}`, 
              description: f.description || f.desc || f.summary || '',
              requirements: caps
            };
            console.log('[parseEpics] Created epic:', JSON.stringify(epic));
            return epic;
          });
          console.log('[parseEpics] Final result count:', result.length, 'total features:', result.reduce((sum, e) => sum + e.requirements.length, 0));
          return result;
        }
        if (typeof features === 'object' && !Array.isArray(features)) {
          // If it's an object with named features
          console.log('[parseEpics] Processing as object');
          return Object.entries(features).map(([key, value]) => ({
            name: key,
            description: typeof value === 'string' ? value : (value.description || JSON.stringify(value)),
            requirements: value.capabilities || value.requirements || []
          }));
        }
        if (typeof features === 'string') {
          console.log('[parseEpics] Processing as string');
          const lines = features.split(/[•\n]/).filter(l => l.trim());
          return lines.map((line, i) => {
            const parts = line.split(':');
            if (parts.length > 1) {
              return { name: parts[0].trim(), description: parts.slice(1).join(':').trim(), requirements: [] };
            }
            return { name: `Feature ${i + 1}`, description: line.trim(), requirements: [] };
          });
        }
        console.log('[parseEpics] Unknown format, returning empty');
        return [];
      };

      // Parse metrics into structured array
      const parseGoals = (metrics) => {
        if (!metrics) return [];
        if (Array.isArray(metrics)) {
          return metrics.map((m, i) => {
            if (typeof m === 'string') return { name: m, description: '', metricsList: [] };
            // Handle objects with category and metrics array
            if (m.category && m.metrics) {
              return {
                name: m.category,
                description: '',
                metricsList: Array.isArray(m.metrics) ? m.metrics : [m.metrics]
              };
            }
            return { 
              name: m.name || m.metric || m.category || `Metric ${i + 1}`, 
              description: m.description || m.target || '',
              metricsList: m.metrics || [],
              target: m.target
            };
          });
        }
        if (typeof metrics === 'object' && !Array.isArray(metrics)) {
          return Object.entries(metrics).map(([key, value]) => ({
            name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            description: typeof value === 'string' ? value : '',
            metricsList: Array.isArray(value) ? value : (value.metrics || [])
          }));
        }
        if (typeof metrics === 'string') {
          const lines = metrics.split(/[•\n]/).filter(l => l.trim());
          return lines.map((line, i) => ({
            name: line.trim(),
            description: '',
            metricsList: []
          }));
        }
        return [];
      };
      
      // Populate Step 2 fields with AI-extracted data
      setExtractedName(extracted.name || productName || 'New Product');
      setExtractedVision(extracted.high_level_vision || productVision);
      
      const parsedAudienceData = parseAudience(extracted.target_audience);
      const parsedEpicsData = parseEpics(extracted.extracted_features);
      const parsedGoalsData = parseGoals(extracted.extracted_metrics);
      
      console.log('[handleAnalyze] FULL extracted data:', JSON.stringify(extracted, null, 2));
      console.log('[handleAnalyze] extracted.extracted_features type:', typeof extracted.extracted_features);
      console.log('[handleAnalyze] extracted.extracted_features:', JSON.stringify(extracted.extracted_features));
      console.log('[handleAnalyze] extracted.extracted_metrics type:', typeof extracted.extracted_metrics);
      console.log('[handleAnalyze] extracted.extracted_metrics:', JSON.stringify(extracted.extracted_metrics));
      console.log('[handleAnalyze] Parsed epics:', parsedEpicsData);
      console.log('[handleAnalyze] Parsed epics length:', parsedEpicsData.length);
      console.log('[handleAnalyze] Parsed goals:', parsedGoalsData);
      console.log('[handleAnalyze] Parsed goals length:', parsedGoalsData.length);
      
      // Set the extracted data arrays
      setExtractedAudience(parsedAudienceData);
      setExtractedEpics(parsedEpicsData);
      setExtractedGoals(parsedGoalsData);
      
      console.log('[handleAnalyze] After setting epics, parsedEpicsData was:', JSON.stringify(parsedEpicsData));
      
      // Try to generate personas based on extracted data
      let personas = [];
      if (parsedAudienceData.length > 0 || extracted.high_level_vision) {
        try {
          // Build a comprehensive vision string for persona generation
          const visionForPersonas = [
            extracted.high_level_vision || productVision,
            extracted.name ? `Product: ${extracted.name}` : '',
          ].filter(Boolean).join('\n\n');
          
          // Build audience string for context
          const audienceStr = parsedAudienceData.map(a => 
            `${a.segment_name}: ${a.description}${a.attributes?.length ? ` (${a.attributes.join(', ')})` : ''}`
          ).join('\n');
          
          // Pass structured segments for persona generation
          const personaResponse = await copilotApi.generatePersonas({
            product_vision: visionForPersonas,
            target_audience: audienceStr || 'General business users',
            audience_segments: parsedAudienceData.map(a => ({
              segment_name: a.segment_name,
              description: a.description || ''
            }))
          });
          personas = personaResponse.data.personas || [];
          setGeneratedPersonas(personas);
        } catch (personaError) {
          console.error('Error generating personas:', personaError);
          setGeneratedPersonas([]);
        }
      }
      
      // Cleanup and move to step 2
      if (elapsedInterval) clearInterval(elapsedInterval);
      setShowProcessingModal(false);
      setProcessingAI(false);
      setStep(2);
      toast.success('AI has extracted structured information');
      
      // Check feature limits after AI extraction
      const totalFeatures = parsedEpicsData.reduce((sum, epic) => sum + (epic.requirements?.length || 0), 0);
      setTotalNewFeatures(totalFeatures);
      
      // Refresh usage and check if we exceed the limit
      try {
        const usageResponse = await featuresApi.getUsage();
        const usage = usageResponse.data;
        setFeatureUsage(usage);
        
        if (usage.plan_tier === 'free' && totalFeatures > usage.remaining) {
          // Show the feature limit modal
          setShowFeatureLimitModal(true);
        }
      } catch (usageError) {
        console.error('Error checking feature usage:', usageError);
      }
      
    } catch (error) {
      console.error('Error processing with AI:', error);
      console.error('Error stack:', error.stack);
      console.error('Error message:', error.message);
      if (elapsedInterval) clearInterval(elapsedInterval);
      setShowProcessingModal(false);
      setProcessingAI(false);
      
      // Fallback to basic extraction if API fails - provide meaningful starter content
      setExtractedName(productName || 'New Product');
      setExtractedVision(productVision || 'Define your product vision here.');
      setExtractedAudience([{ segment_name: 'Primary Users', description: 'Define your target audience', type: 'Primary' }]);
      setExtractedEpics([
        { name: 'Core Epic 1', description: 'Describe a major feature area', requirements: ['Requirement A', 'Requirement B'] },
        { name: 'Core Epic 2', description: 'Describe another feature area', requirements: [] }
      ]);
      setExtractedGoals([
        { name: 'User Engagement', description: 'How users interact with the product', metricsList: ['Daily Active Users', 'Session Duration'] },
        { name: 'Business Impact', description: 'Business outcomes', metricsList: ['Revenue Growth', 'Customer Retention'] }
      ]);
      setGeneratedPersonas([
        { name: 'Primary User', role: 'Target Customer', segment_name: 'Primary Users', goals: 'Define goals', pain_points: 'Define pain points', context: 'Define context' }
      ]);
      
      setStep(2);
      toast.warning('Using basic extraction - AI service unavailable');
    }
  };

  // Handle creating an empty product (skip AI)
  const handleCreateEmptyProduct = async () => {
    // Validate: name is required for empty product
    if (!productName.trim()) {
      toast.error('Product name is required when skipping AI');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create product with just name and optional vision, skip AI extraction
      const response = await productsApi.create({
        name: productName.trim(),
        high_level_vision: productVision.trim() || '',
        skip_ai_extraction: true
      });
      
      if (response.data?.product_id) {
        toast.success('Product created successfully!', {
          description: 'You can now add epics, features, and personas manually.'
        });
        navigate(`/products/${response.data.product_id}`);
      } else {
        throw new Error('No product ID returned');
      }
    } catch (error) {
      console.error('Error creating empty product:', error);
      const message = error.response?.data?.detail || 'Failed to create product';
      
      // Check if it's a limit error
      if (message.includes('limit') || message.includes('Free tier')) {
        setShowProductLimitModal(true);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== EDIT MODAL FUNCTIONS ====================
  
  // Open edit modal
  const openEditModal = (type, index = null) => {
    let data = {};
    if (type === 'vision') {
      data = { vision: extractedVision };
    } else if (type === 'epic') {
      if (index !== null) {
        const epic = extractedEpics[index];
        // Normalize to use 'requirements' - this is what the form expects
        data = { 
          ...epic, 
          requirements: epic.requirements || epic.capabilities || [] 
        };
      } else {
        data = { name: '', description: '', requirements: [] };
      }
    } else if (type === 'persona') {
      if (index !== null) {
        const persona = { ...generatedPersonas[index] };
        // Convert segment_name to segment_tags array for editing
        if (persona.segment_name && !persona.segment_tags) {
          persona.segment_tags = [persona.segment_name];
        } else if (!persona.segment_tags) {
          persona.segment_tags = [];
        }
        data = persona;
      } else {
        data = { name: '', role: '', segment_tags: [], goals: '', pain_points: '', context: '' };
      }
    } else if (type === 'goal') {
      data = index !== null ? { ...extractedGoals[index] } : { name: '', description: '', metricsList: [] };
    }
    
    setEditModal({ isOpen: true, type, index, data });
    setEditFormData(data);
    setCopilotMessages([{
      role: 'assistant',
      content: `I'm here to help you ${index !== null ? 'refine' : 'create'} this ${type}. Ask me anything or request changes like "make this more concise" or "suggest improvements".`
    }]);
    setCopilotInput('');
    setDeleteConfirm(false);
  };
  
  // Close edit modal
  const closeEditModal = () => {
    setEditModal({ isOpen: false, type: null, index: null, data: null });
    setEditFormData({});
    setCopilotMessages([]);
    setDeleteConfirm(false);
  };

  // Feature limit modal handlers
  const handleRemoveFeatures = () => {
    setShowFeatureLimitModal(false);
    toast.info('Remove features from your epics', { 
      description: `You need to remove ${totalNewFeatures - (featureUsage?.remaining || 0)} features to continue with the free tier.`
    });
    // User will manually remove features from epics
  };

  const handleUpgradeComplete = () => {
    // Called when one-click upgrade succeeds
    // Close the modal and refresh usage data
    setShowProductLimitModal(false);
    setShowFeatureLimitModal(false);
    
    // Refresh feature usage
    featuresApi.getUsage().then(response => {
      setFeatureUsage(response.data);
    }).catch(console.error);
  };

  const handleShowUpgradeModal = (limitType = 'products') => {
    setCurrentLimitType(limitType);
    if (limitType === 'products') {
      setShowProductLimitModal(true);
    } else if (limitType === 'features') {
      setShowFeatureLimitModal(true);
    }
  };

  // Recalculate total features when epics change
  useEffect(() => {
    const total = extractedEpics.reduce((sum, epic) => sum + (epic.requirements?.length || 0), 0);
    setTotalNewFeatures(total);
  }, [extractedEpics]);
  
  // Save edited item
  const saveEditedItem = () => {
    const { type, index } = editModal;
    
    if (type === 'vision') {
      setExtractedVision(editFormData.vision);
    } else if (type === 'epic') {
      // Ensure we keep either requirements or capabilities (prioritize requirements if both exist)
      const epicToSave = { 
        ...editFormData,
        // Normalize to use 'requirements' as the canonical field for features
        requirements: editFormData.requirements || editFormData.capabilities || []
      };
      
      // Calculate new total features after this save
      const newFeatureCount = epicToSave.requirements.length;
      let otherFeaturesCount = 0;
      extractedEpics.forEach((epic, i) => {
        if (i !== index) {
          otherFeaturesCount += (epic.requirements?.length || 0);
        }
      });
      const totalAfterSave = otherFeaturesCount + newFeatureCount;
      
      // Check if this would exceed the limit
      if (featureUsage && featureUsage.plan_tier === 'free' && totalAfterSave > featureUsage.remaining) {
        // Update epics first so modal shows correct data
        if (index !== null) {
          setExtractedEpics(prev => prev.map((f, i) => i === index ? epicToSave : f));
        } else {
          setExtractedEpics(prev => [...prev, epicToSave]);
        }
        setTotalNewFeatures(totalAfterSave);
        setShowFeatureLimitModal(true);
        closeEditModal();
        return;
      }
      
      if (index !== null) {
        setExtractedEpics(prev => prev.map((f, i) => i === index ? epicToSave : f));
      } else {
        setExtractedEpics(prev => [...prev, epicToSave]);
      }
    } else if (type === 'persona') {
      // Convert segment_tags back to segment_name for display, but keep both
      const personaToSave = { 
        ...editFormData,
        segment_name: editFormData.segment_tags?.[0] || '', // Primary tag for card display
      };
      
      // Update product-level tags
      if (editFormData.segment_tags?.length > 0) {
        setProductTags(prev => {
          const newTags = new Set(prev);
          editFormData.segment_tags.forEach(tag => newTags.add(tag));
          return newTags;
        });
      }
      
      if (index !== null) {
        setGeneratedPersonas(prev => prev.map((p, i) => i === index ? personaToSave : p));
      } else {
        setGeneratedPersonas(prev => [...prev, personaToSave]);
      }
    } else if (type === 'goal') {
      if (index !== null) {
        setExtractedGoals(prev => prev.map((m, i) => i === index ? editFormData : m));
      } else {
        setExtractedGoals(prev => [...prev, editFormData]);
      }
    }
    
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} ${index !== null ? 'updated' : 'added'} successfully`);
    closeEditModal();
  };
  
  // Rebuild productTags whenever personas change (for cleanup of orphaned tags)
  useEffect(() => {
    const allTags = new Set();
    generatedPersonas.forEach(p => {
      if (p.segment_tags?.length > 0) {
        p.segment_tags.forEach(tag => allTags.add(tag));
      } else if (p.segment_name) {
        allTags.add(p.segment_name);
      }
    });
    // Also include tags from audience segments
    extractedAudience.forEach(a => {
      if (a.segment_name) allTags.add(a.segment_name);
    });
    setProductTags(allTags);
  }, [generatedPersonas, extractedAudience]);
  
  // Delete item
  const deleteItem = () => {
    const { type, index } = editModal;
    
    if (type === 'epic') {
      setExtractedEpics(prev => prev.filter((_, i) => i !== index));
    } else if (type === 'persona') {
      setGeneratedPersonas(prev => prev.filter((_, i) => i !== index));
    } else if (type === 'goal') {
      setExtractedGoals(prev => prev.filter((_, i) => i !== index));
    }
    
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
    closeEditModal();
  };
  
  // Scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [copilotMessages]);
  
  // Handle Co-Pilot chat in edit modal
  const handleEditCopilotSend = async () => {
    if (!copilotInput.trim() || copilotLoading) return;
    
    const userMessage = copilotInput;
    setCopilotInput('');
    setCopilotMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setCopilotLoading(true);
    
    try {
      // Build context for the AI - include FULL product context + current item + conversation
      const itemContext = JSON.stringify(editFormData, null, 2);
      
      // Format conversation history properly for the AI
      const conversationForAI = copilotMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      // Add the new user message to history
      conversationForAI.push({ role: 'user', content: userMessage });
      
      // Build complete product context so AI understands the full picture
      const epicsContext = extractedEpics.map(e => `- ${e.name}: ${e.description || ''} [Features: ${(e.requirements || []).join(', ') || 'none'}]`).join('\n');
      const personasContext = generatedPersonas.map(p => `- ${p.name} (${p.segment_name || p.role}): ${p.goals || ''}`).join('\n');
      const goalsContext = extractedGoals.map(g => `- ${g.name}: ${(g.metricsList || []).join(', ')}`).join('\n');
      
      const fullContext = `
=== FULL PRODUCT CONTEXT ===
Product Name: ${extractedName}

High-Level Vision:
${extractedVision}

Epics (Feature Areas):
${epicsContext || 'None defined yet'}

User Personas:
${personasContext || 'None defined yet'}

Goals & Success Metrics:
${goalsContext || 'None defined yet'}

=== CURRENT ${editModal.type.toUpperCase()} BEING EDITED ===
${itemContext}

=== CONVERSATION HISTORY ===
${conversationForAI.map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n\n')}
`;
      
      // Use unified co-pilot endpoint with edit_item context
      const response = await unifiedCopilotApi.chat('edit_item', editModal.type, {
        message: fullContext,
        conversation_history: conversationForAI
      });
      
      const aiResponse = response.data.response;
      
      // Check for update blocks in different formats
      // Format 1: ```update ... ``` (new format)
      // Format 2: [PROPOSED_UPDATE] ... [/PROPOSED_UPDATE] (old format)
      let proposedData = null;
      let displayContent = aiResponse;
      
      // Try new format first (```update```)
      const updateBlockMatch = aiResponse.match(/```update\s*([\s\S]*?)```/);
      if (updateBlockMatch) {
        try {
          proposedData = JSON.parse(updateBlockMatch[1].trim());
          displayContent = aiResponse.replace(/```update\s*[\s\S]*?```/g, '').trim();
        } catch (e) {
          console.error('Failed to parse update block:', e);
        }
      }
      
      // Fallback to old format
      if (!proposedData) {
        const proposalMatch = aiResponse.match(/\[PROPOSED_UPDATE\]([\s\S]*?)\[\/PROPOSED_UPDATE\]/);
        if (proposalMatch) {
          try {
            proposedData = JSON.parse(proposalMatch[1]);
            displayContent = aiResponse.replace(/\[PROPOSED_UPDATE\][\s\S]*?\[\/PROPOSED_UPDATE\]/, '').trim();
          } catch (e) {
            console.error('Failed to parse old format update:', e);
          }
        }
      }
      
      // Add message with or without proposed update
      setCopilotMessages(prev => [...prev, { 
        role: 'assistant', 
        content: displayContent,
        proposedUpdate: proposedData
      }]);
    } catch (error) {
      console.error('Error with Co-Pilot:', error);
      setCopilotMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I encountered an issue. Could you rephrase your request?" 
      }]);
    } finally {
      setCopilotLoading(false);
    }
  };
  
  // Apply proposed update from AI
  const applyProposedUpdate = (proposedData) => {
    setEditFormData(prev => ({ ...prev, ...proposedData }));
    setCopilotMessages(prev => [...prev, { 
      role: 'assistant', 
      content: "✓ Changes applied! Review the updates in the form and save when ready." 
    }]);
  };

  // Step 2: Create product with refined data
  const handleCreateProduct = async () => {
    if (!extractedName.trim()) {
      toast.error('Please provide a product name');
      return;
    }

    // Check feature limits before creating
    const totalFeatures = extractedEpics.reduce((sum, epic) => sum + (epic.requirements?.length || 0), 0);
    
    if (featureUsage && featureUsage.plan_tier === 'free' && totalFeatures > featureUsage.remaining) {
      setTotalNewFeatures(totalFeatures);
      setShowFeatureLimitModal(true);
      return;
    }

    setShowPersonaGeneration(true);
    setLoading(true);
    setSaveStep(0);

    try {
      // Format audience for product record
      const audienceStr = extractedAudience.map(a => 
        `${a.segment_name || a.persona || 'Segment'}: ${a.description || ''}`
      ).join('\n\n');
      
      // Step 0: Creating product
      setSaveStep(0);
      
      // 1. Create the Product (skip AI extraction since Step 1 already did it)
      let productResponse;
      try {
        productResponse = await productsApi.create({
          name: extractedName,
          high_level_vision: extractedVision,
          target_audience: audienceStr,
          skip_ai_extraction: true  // Frontend already did extraction in Step 1
        });
      } catch (productError) {
        console.error('[DEBUG] Product creation failed:', productError);
        const errorDetail = productError.response?.data?.detail || productError.message;
        
        // Check if this is a product limit error
        if (errorDetail && errorDetail.includes('Free tier limit') && errorDetail.includes('product')) {
          // Fetch actual usage data for the modal
          try {
            const usageRes = await fetch(`${API_BASE_URL}/api/subscription/status`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const usageData = await usageRes.json();
            setProductUsage({
              plan_tier: usageData.usage?.plan_tier || 'free',
              max_products: usageData.usage?.products_limit || 2,
              products_used: usageData.usage?.products_used || 0
            });
          } catch (e) {
            // Fallback to extracting from error message
            const limitMatch = errorDetail.match(/\((\d+) products?\)/);
            const maxProducts = limitMatch ? parseInt(limitMatch[1]) : 2;
            setProductUsage({
              plan_tier: 'free',
              max_products: maxProducts,
              products_used: maxProducts
            });
          }
          setShowProductLimitModal(true);
          setShowPersonaGeneration(false);
          setLoading(false);
          return;
        }
        
        toast.error(`Failed to create product: ${errorDetail}`);
        setShowPersonaGeneration(false);
        setLoading(false);
        return;
      }

      console.log('[DEBUG] Product response:', productResponse);
      console.log('[DEBUG] Product response data:', productResponse.data);
      
      const productId = productResponse.data.product_id;
      console.log('[DEBUG] Product ID extracted:', productId);
      
      if (!productId) {
        console.error('[DEBUG] CRITICAL: productId is undefined or null!');
        toast.error('Failed to create product - no product ID returned');
        setShowPersonaGeneration(false);
        setLoading(false);
        return;
      }

      // Step 1: Creating Goals
      setSaveStep(1);
      
      // 2. Create Goals (was Success Metrics)
      for (const goal of extractedGoals) {
        try {
          console.log('[DEBUG] Creating goal:', goal.name);
          
          // Convert metrics to KPIs
          const kpis = (goal.metricsList || []).map((metric, idx) => ({
            kpi_id: `kpi_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
            metric_name: typeof metric === 'string' ? metric : (metric.name || metric.metric_name || String(metric)),
            baseline_value: null,
            target_value: null,
            current_value: null,
            unit: null
          }));
          
          console.log('[DEBUG] Creating KPIs for goal:', goal.name, kpis.length, 'KPIs');
          
          const goalResponse = await goalsApi.create(productId, {
            title: goal.name,
            description: goal.description || '',
            success_metrics: goal.metricsList || [],
            kpis: kpis  // Add KPIs from metrics
          });
          console.log('[DEBUG] Goal created successfully with KPIs:', goalResponse.data);
        } catch (goalError) {
          console.error('[DEBUG] Error creating goal:', goal.name, goalError.response?.data || goalError.message);
        }
      }
      // Step 2: Creating Epics
      setSaveStep(2);

      // 3. Create Epics and their Features (new hierarchy: Product -> Epic -> Feature -> Requirements)
      console.log('[handleCreateProduct] Creating epics, count:', extractedEpics.length);
      console.log('[handleCreateProduct] Full extractedEpics data:', JSON.stringify(extractedEpics, null, 2));
      for (const epic of extractedEpics) {
        console.log('[handleCreateProduct] Processing Epic:', epic.name);
        console.log('[handleCreateProduct] Epic requirements:', epic.requirements);
        console.log('[handleCreateProduct] Epic capabilities:', epic.capabilities);
        console.log('[handleCreateProduct] Epic full object:', JSON.stringify(epic, null, 2));
        try {
          const epicResponse = await epicsApi.createForProduct(productId, {
            title: epic.name,
            description: epic.description || ''
          });

          // Create features under the epic
          const epicId = epicResponse.data.epic_id;
          const featureItems = epic.requirements || [];
          console.log('[handleCreateProduct] Epic created:', epicId, 'featureItems:', featureItems.length, featureItems);
          if (epicId && featureItems.length > 0) {
            for (const item of featureItems) {
              const featureText = typeof item === 'string' ? item : (item.title || item.name || item.description || '');
              console.log('[handleCreateProduct] Creating feature:', featureText);
              if (featureText && featureText.trim()) {
                try {
                  const featResp = await featuresApi.create(epicId, {
                    title: featureText.trim(),
                    description: featureText.trim(),
                    user_story: '',
                    requirements: [],  // Embedded requirements (acceptance criteria) - empty initially
                    status: 'Backlog',
                    product_id: productId  // Required for product-level feature queries
                  });
                  console.log('[handleCreateProduct] Feature created:', featResp.data?.feature_id);
                } catch (featError) {
                  console.error('[handleCreateProduct] Error creating feature:', featError);
                }
              }
            }
          } else {
            console.log('[handleCreateProduct] No features to create for epic:', epicId);
          }
        } catch (epicError) {
          console.error('[handleCreateProduct] Error creating epic:', epicError);
        }
      }

      // Step 3: Creating Personas
      setSaveStep(3);

      // 4. Create Personas
      for (const persona of generatedPersonas) {
        try {
          await personasApi.create(productId, {
            name: persona.name,
            role: persona.role,
            segment_name: persona.segment_name,
            goals: persona.goals,
            pain_points: persona.pain_points,
            context: persona.context
          });
        } catch (personaError) {
          console.error('Error creating persona:', personaError);
        }
      }

      // Step 4: Finalizing
      setSaveStep(4);
      
      toast.success('Product created successfully!', {
        description: 'Your new product is ready. Start adding epics and features!'
      });
      console.log('[DEBUG] About to navigate to dashboard');
      
      // Reset loading state before navigation
      setShowPersonaGeneration(false);
      setLoading(false);
      
      // Navigate to Dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating product:', error);
      // More specific error message
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create product';
      toast.error(errorMsg);
      setShowPersonaGeneration(false);
      setLoading(false);
    }
  };

  // Processing Modal - Custom implementation to avoid flash
  // eslint-disable-next-line react/no-unstable-nested-components
  const ProcessingModal = () => {
    if (!showProcessingModal) return null;
    
    const steps = processingSteps; // Use frozen steps from state
    const currentStepData = steps[processingStep] || {};
    const isCrawling = currentStepData.key === 'crawl';
    
    return (
      <div className="fixed inset-0 z-50">
        {/* Overlay */}
        <div className="fixed inset-0 bg-black/80" />
        
        {/* Modal Content */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div 
            className={cn("w-full max-w-md rounded-lg shadow-lg p-6", colors.bgCard)}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-white animate-pulse" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                AI is Analyzing Your Product
              </h3>
              
              <p className={cn("text-sm text-center mb-6", colors.textSecondary)}>
                This may take <strong>1-5 minutes</strong> depending on content size.
                <br />Please don&apos;t close this window.
              </p>
              
              <div className="w-full space-y-3">
                {steps.map((step, index) => (
                  <div 
                    key={step.key}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      index < processingStep 
                        ? 'bg-green-50 dark:bg-green-900/20' 
                        : index === processingStep 
                          ? 'bg-blue-50 dark:bg-blue-900/20' 
                          : 'bg-gray-50 dark:bg-gray-700/50'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      index < processingStep 
                        ? 'bg-green-500' 
                        : index === processingStep 
                          ? 'bg-blue-500' 
                          : 'bg-gray-300 dark:bg-gray-600'
                    }`}>
                      {index < processingStep ? (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : index === processingStep ? (
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm block ${
                        index <= processingStep 
                          ? 'text-gray-900 dark:text-white' 
                          : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {step.label}
                      </span>
                      {/* Real-time crawl progress */}
                      {index === processingStep && step.key === 'crawl' && crawlProgress && (
                        <div className="mt-1 space-y-1">
                          <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                            {crawlProgress.message}
                          </p>
                          <div className="flex gap-3 text-xs text-gray-500">
                            <span>Pages: {crawlProgress.pagesFetched}</span>
                            <span>{(crawlProgress.totalChars / 1000).toFixed(1)}K chars</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Processing with Gemini AI...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Epic Card Component with edit button
  // eslint-disable-next-line react/no-unstable-nested-components
  const EpicCard = ({ epic, index }) => {
    const isExpanded = expandedEpics.has(index);
    const featuresCount = (epic.requirements || []).length;
    
    return (
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <div className="flex items-center bg-amber-50 dark:bg-amber-900/20">
          <button
            onClick={() => toggleEpicExpand(index)}
            className="flex-1 p-3 flex items-center gap-3 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
              <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white text-sm">{epic.name}</p>
              <p className={cn("text-xs", colors.textMuted)}>
                {featuresCount} feature{featuresCount !== 1 ? 's' : ''}
              </p>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            )}
          </button>
          <button
            onClick={() => openEditModal('epic', index)}
            className="p-3 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
          >
            <Pencil className="w-4 h-4 text-gray-500 hover:text-amber-600" />
          </button>
        </div>
        
        {isExpanded && (
          <div className={cn("p-4 space-y-3 text-sm", colors.bgCard)}>
            {epic.description && (
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Description</p>
                <p className={cn("", colors.textSecondary)}>{epic.description}</p>
              </div>
            )}
            {(epic.requirements || []).length > 0 && (
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Features</p>
                <ul className={cn(colors.textSecondary, "list-disc list-inside space-y-1")}>
                  {(epic.requirements || []).map((feat, i) => <li key={i}>{feat}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Persona Card Component with edit button
  // eslint-disable-next-line react/no-unstable-nested-components
  const PersonaCard = ({ persona, index }) => {
    const isExpanded = expandedPersonas.has(index);
    
    return (
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <div className="flex items-center bg-green-50 dark:bg-green-900/20">
          <button
            onClick={() => togglePersonaExpand(index)}
            className="flex-1 p-3 flex items-center gap-3 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
              <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white text-sm">{persona.name}</p>
              <p className={cn("text-xs", colors.textMuted)}>{persona.role}</p>
              {persona.segment_name && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full">
                  {persona.segment_name}
                </span>
              )}
            </div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            )}
          </button>
          <button
            onClick={() => openEditModal('persona', index)}
            className="p-3 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            <Pencil className="w-4 h-4 text-gray-500 hover:text-green-600" />
          </button>
        </div>
        
        {isExpanded && (
          <div className={cn("p-4 space-y-3 text-sm", colors.bgCard)}>
            {persona.goals && (
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Goals</p>
                <p className={cn("", colors.textSecondary)}>{persona.goals}</p>
              </div>
            )}
            {persona.pain_points && (
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Pain Points</p>
                <p className={cn("", colors.textSecondary)}>{persona.pain_points}</p>
              </div>
            )}
            {persona.context && (
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Context</p>
                <p className={cn("", colors.textSecondary)}>{persona.context}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Goal Card Component with edit button
  // eslint-disable-next-line react/no-unstable-nested-components
  const GoalCard = ({ goal, index }) => {
    const isExpanded = expandedGoals.has(index);
    const hasMetrics = goal.metricsList && goal.metricsList.length > 0;
    
    return (
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <div className="flex items-center bg-indigo-50 dark:bg-indigo-900/20">
          <button
            onClick={() => toggleGoalExpand(index)}
            className="flex-1 p-3 flex items-center gap-3 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
              <BarChart3 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white text-sm">{goal.name}</p>
              {hasMetrics && (
                <p className={cn("text-xs", colors.textMuted)}>{goal.metricsList.length} metrics</p>
              )}
            </div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            )}
          </button>
          <button
            onClick={() => openEditModal('goal', index)}
            className="p-3 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
          >
            <Pencil className="w-4 h-4 text-gray-500 hover:text-indigo-600" />
          </button>
        </div>
        
        {isExpanded && (
          <div className={cn("p-4 text-sm space-y-3", colors.bgCard)}>
            {goal.description && (
              <p className={cn("", colors.textSecondary)}>{goal.description}</p>
            )}
            {hasMetrics && (
              <div>
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">Success Metrics</p>
                <ul className={cn(colors.textSecondary, "list-disc list-inside space-y-1")}>
                  {goal.metricsList.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Persona generation/creation screen
  // Save progress steps
  const saveSteps = [
    { label: 'Creating product...', key: 'product' },
    { label: `Creating ${extractedGoals.length} goal(s)...`, key: 'goals' },
    { label: `Creating ${extractedEpics.length} epic(s) with features...`, key: 'epics' },
    { label: `Creating ${generatedPersonas.length} persona(s)...`, key: 'personas' },
    { label: 'Finalizing...', key: 'finalize' },
  ];

  if (showPersonaGeneration) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Creating Product" />
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-md mx-auto">
              <Card className={cn("p-8 border", colors.bgCard, colors.borderPrimary)}>
                <div className="flex flex-col items-center py-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-6">
                    <Sparkles className="w-8 h-8 text-white animate-pulse" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Creating Your Product
                  </h3>
                  
                  <p className={cn("text-sm text-center mb-6", colors.textSecondary)}>
                    Setting up <strong>{extractedName}</strong>
                  </p>
                  
                  <div className="w-full space-y-3">
                    {saveSteps.map((step, index) => (
                      <div 
                        key={step.key}
                        className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                          index < saveStep 
                            ? 'bg-green-50 dark:bg-green-900/20' 
                            : index === saveStep 
                              ? 'bg-blue-50 dark:bg-blue-900/20' 
                              : 'bg-gray-50 dark:bg-gray-700/50'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                          index < saveStep 
                            ? 'bg-green-500' 
                            : index === saveStep 
                              ? 'bg-blue-500' 
                              : 'bg-gray-300 dark:bg-gray-600'
                        }`}>
                          {index < saveStep ? (
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : index === saveStep ? (
                            <Loader2 className="w-4 h-4 text-white animate-spin" />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-gray-400" />
                          )}
                        </div>
                        <span className={`text-sm ${
                          index <= saveStep 
                            ? 'text-gray-900 dark:text-white' 
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {step.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 flex items-center gap-2 text-xs text-gray-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Saving to database...</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: AI Interpretation Review with Co-Pilot
  if (step === 2) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Review AI Interpretation" />
          <div className="flex-1 flex overflow-hidden">
            {/* Main Content - Editable Fields */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="max-w-3xl mx-auto">
                <Card className={cn("p-8 border", colors.bgCard, colors.borderPrimary)}>
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
                        2
                      </div>
                      <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Review & Refine AI Extraction</h3>
                    </div>
                    <p className={cn("text-sm", colors.textSecondary)}>
                      The AI has analyzed your input and extracted structured information. Review and refine as needed.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="extractedName" className="text-base">Product Name *</Label>
                      <Input
                        id="extractedName"
                        value={extractedName}
                        onChange={(e) => setExtractedName(e.target.value)}
                        className="mt-2"
                      />
                    </div>

                    {/* High-Level Vision as display card */}
                    <div>
                      <Label className="text-base mb-3 block">High-Level Vision *</Label>
                      <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                        <div className="flex items-start bg-blue-50 dark:bg-blue-900/20 p-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-800 dark:text-gray-200 text-sm whitespace-pre-wrap">
                              {extractedVision || 'No vision defined yet.'}
                            </p>
                          </div>
                          <button
                            onClick={() => openEditModal('vision', 0)}
                            className="ml-3 p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors shrink-0"
                          >
                            <Pencil className="w-4 h-4 text-gray-500 hover:text-blue-600" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Epics */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-base flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Epics
                          <span className="text-sm font-normal text-gray-500">({extractedEpics.length})</span>
                        </Label>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openEditModal('epic', null)}
                          className="h-7 text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      
                      {/* Feature Usage Indicator - Only show for free tier when approaching or over limit */}
                      {featureUsage && featureUsage.plan_tier === 'free' && totalNewFeatures > 0 && (
                        <div className={cn(
                          "mb-3 p-3 rounded-lg border text-sm",
                          totalNewFeatures > featureUsage.remaining 
                            ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" 
                            : featureUsage.remaining < 10
                              ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                              : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                        )}>
                          <div className="flex items-center justify-between">
                            <span className={
                              totalNewFeatures > featureUsage.remaining 
                                ? "text-red-700 dark:text-red-300" 
                                : featureUsage.remaining < 10
                                  ? "text-amber-700 dark:text-amber-300"
                                  : "text-blue-700 dark:text-blue-300"
                            }>
                              Features: <strong>{totalNewFeatures}</strong> to create / <strong>{featureUsage.remaining}</strong> available on free tier
                            </span>
                            {totalNewFeatures > featureUsage.remaining && (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                ⚠ {totalNewFeatures - featureUsage.remaining} over limit
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        {extractedEpics.map((epic, index) => (
                          <EpicCard key={index} epic={epic} index={index} />
                        ))}
                        {extractedEpics.length === 0 && (
                          <div className={cn("text-sm p-4 border border-dashed rounded-lg text-center", colors.textMuted, colors.borderPrimary)}>
                            No epics yet. Click &quot;+ Add&quot; to create one.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* User Personas with Audience Tags */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-base flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          User Personas
                          <span className="text-sm font-normal text-gray-500">({generatedPersonas.length})</span>
                        </Label>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openEditModal('persona', null)}
                          className="h-7 text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {generatedPersonas.map((persona, index) => (
                          <PersonaCard key={index} persona={persona} index={index} />
                        ))}
                        {generatedPersonas.length === 0 && (
                          <div className={cn("text-sm p-4 border border-dashed rounded-lg text-center", colors.textMuted, colors.borderPrimary)}>
                            No personas yet. Click &quot;+ Add&quot; to create one.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Goals */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-base flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          Goals
                          <span className="text-sm font-normal text-gray-500">({extractedGoals.length})</span>
                        </Label>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => openEditModal('goal', null)}
                          className="h-7 text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {extractedGoals.map((goal, index) => (
                          <GoalCard key={index} goal={goal} index={index} />
                        ))}
                        {extractedGoals.length === 0 && (
                          <div className={cn("text-sm p-4 border border-dashed rounded-lg text-center", colors.textMuted, colors.borderPrimary)}>
                            No goals yet. Click &quot;+ Add&quot; to create one.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleCreateProduct}
                      disabled={loading}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 gap-2"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> Create Product</>
                      )}
                    </Button>
                  </div>
                </Card>
              </div>
            </div>

            {/* Co-Pilot Sidebar removed - will be reimplemented */}
          </div>
        </div>
        
        <ProcessingModal />
        
        {/* Edit Modal */}
        <EditModal
          isOpen={editModal.isOpen}
          type={editModal.type}
          index={editModal.index}
          formData={editFormData}
          setFormData={setEditFormData}
          onSave={saveEditedItem}
          onDelete={deleteItem}
          onClose={closeEditModal}
          copilotMessages={copilotMessages}
          copilotInput={copilotInput}
          setCopilotInput={setCopilotInput}
          copilotLoading={copilotLoading}
          onCopilotSend={handleEditCopilotSend}
          onApplyUpdate={applyProposedUpdate}
          deleteConfirm={deleteConfirm}
          setDeleteConfirm={setDeleteConfirm}
          availableTags={Array.from(productTags)}
        />
        
        {/* Feature Limit Modal */}
        <FeatureLimitModal
          isOpen={showFeatureLimitModal}
          onClose={() => setShowFeatureLimitModal(false)}
          usage={featureUsage}
          newFeaturesCount={totalNewFeatures}
          epics={extractedEpics}
          onRemoveFeatures={handleRemoveFeatures}
          wizardState={getWizardState()}
          returnPath="/products/new"
          onUpgradeComplete={handleUpgradeComplete}
        />
        
        {/* Product Limit Modal - Using new UpgradeContextModal */}
        <UpgradeContextModal
          isOpen={showProductLimitModal}
          onClose={() => setShowProductLimitModal(false)}
          limitType="products"
          currentUsage={productUsage?.products_used}
          currentLimit={productUsage?.max_products}
          wizardState={getWizardState()}
          returnPath="/products/new"
          onUpgradeComplete={handleUpgradeComplete}
        />
      </div>
    );
  }

  // Step 1: Initial Input
  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Create New Product" />
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-2xl mx-auto">
            <Card className={cn("p-8 border", colors.bgCard, colors.borderPrimary)}>
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className={cn("flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-medium", colors.accentSolid)}>
                    1
                  </div>
                  <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Tell us about your product</h3>
                </div>
                <p className={cn("text-sm", colors.textSecondary)}>
                  Describe your product vision. The AI will analyze your input and extract structured information for the next step.
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="productName" className={cn("text-base", colors.textPrimary)}>Product Name (Optional)</Label>
                  <Input
                    id="productName"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="e.g., Customer Portal v2 (AI will suggest if blank)"
                    className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  />
                  <p className={cn("text-xs mt-1", colors.textMuted)}>
                    Leave blank and AI will suggest based on your vision
                  </p>
                </div>

                <div>
                  <Label htmlFor="vision" className={cn("text-base", colors.textPrimary)}>High-Level Vision or Description</Label>
                  <Textarea
                    id="vision"
                    value={productVision}
                    onChange={(e) => setProductVision(e.target.value)}
                    placeholder="Describe your product vision, target audience, key value proposition, and goals...

Example: Build a self-service customer portal for B2B SaaS companies that reduces support tickets by 40%..."
                    rows={6}
                    className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  />
                  <p className={cn("text-xs mt-2", colors.textMuted)}>
                    {productVision.split(/\s+/).filter(w => w.trim()).length} words
                  </p>
                </div>

                {/* Sources Section - Combined Files + URLs */}
                <div className="space-y-3">
                  <Label className={cn("text-base", colors.textPrimary)}>Add Sources (Optional)</Label>
                  <p className={cn("text-xs -mt-1", colors.textMuted)}>
                    Upload documents or add website URLs. AI will combine all sources.
                  </p>
                  
                  {/* Sources List */}
                  {(uploadedFiles.length > 0 || sourceUrls.length > 0) && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      {uploadedFiles.map((file, index) => (
                        <div 
                          key={`file-${index}`}
                          className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full border", colors.bgCard, colors.borderPrimary)}
                        >
                          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                          </svg>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="text-gray-400 hover:text-red-500 ml-1"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      {sourceUrls.map((url, index) => (
                        <div 
                          key={`url-${index}`}
                          className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full border border-green-500/30", colors.bgCard)}
                        >
                          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                          <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate">
                            {url.replace(/^https?:\/\//, '')}
                          </span>
                          <button
                            onClick={() => handleRemoveUrl(index)}
                            className="text-gray-400 hover:text-red-500 ml-1"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Source Buttons */}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.pptx"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('file-upload').click()}
                      className={cn("gap-2 border-2", colors.borderSecondary, colors.textSecondary, colors.bgHover)}
                    >
                      <Plus className="w-4 h-4" />
                      Upload File
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowUrlInput(!showUrlInput)}
                      className={cn("gap-2 border-2", colors.borderSecondary, colors.textSecondary, colors.bgHover)}
                    >
                      <Plus className="w-4 h-4" />
                      Add Website URL
                    </Button>
                  </div>
                  
                  {/* URL Input (shown when adding) */}
                  {showUrlInput && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
                      <div className="flex gap-2">
                        <Input
                          value={urlInputValue}
                          onChange={(e) => setUrlInputValue(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
                          placeholder="https://example.com"
                          className="flex-1"
                          autoFocus
                        />
                        <Button
                          onClick={handleAddUrl}
                          disabled={!urlInputValue.trim()}
                          size="sm"
                        >
                          Add
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setShowUrlInput(false); setUrlInputValue(''); }}
                        >
                          Cancel
                        </Button>
                      </div>
                      
                      {/* Advanced Crawl Options */}
                      <div>
                        <button
                          onClick={() => setShowAdvancedCrawl(!showAdvancedCrawl)}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-1"
                        >
                          {showAdvancedCrawl ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          Advanced options
                        </button>
                        {showAdvancedCrawl && (
                          <div className="mt-2 flex items-center gap-4 text-sm">
                            <span className={cn("", colors.textMuted)}>Crawl depth:</span>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="crawlMode"
                                value="single"
                                checked={crawlMode === 'single'}
                                onChange={(e) => setCrawlMode(e.target.value)}
                                className="text-blue-600"
                              />
                              <span className="text-gray-700 dark:text-gray-300">Just this page</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="crawlMode"
                                value="crawl"
                                checked={crawlMode === 'crawl'}
                                onChange={(e) => setCrawlMode(e.target.value)}
                                className="text-blue-600"
                              />
                              <span className="text-gray-700 dark:text-gray-300">Follow links</span>
                              <span className="text-xs text-blue-600">(default)</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Instructions for AI */}
                <div>
                  <Label htmlFor="aiInstructions" className={cn("text-base", colors.textPrimary)}>Instructions for AI (Optional)</Label>
                  <Textarea
                    id="aiInstructions"
                    value={aiInstructions}
                    onChange={(e) => setAiInstructions(e.target.value)}
                    placeholder="e.g., 'Focus on the B2B features only' or 'Use the pitch deck to generate personas'"
                    rows={2}
                    className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  />
                  <p className={cn("text-xs mt-1", colors.textMuted)}>
                    Guide how AI interprets your text, documents, and websites
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                      className={cn("flex-1 border-2", colors.borderSecondary, colors.textSecondary, colors.bgHover)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleProcessWithAI}
                      disabled={(!productVision.trim() && uploadedFiles.length === 0 && sourceUrls.length === 0) || processingAI || loading}
                      className={cn("flex-1 gap-2", colors.accentGradient, colors.accentGradientHover)}
                    >
                      {processingAI ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> Process with AI</>
                      )}
                    </Button>
                  </div>
                  
                  {/* Skip AI option */}
                  <div className="text-center">
                    <button
                      onClick={handleCreateEmptyProduct}
                      disabled={loading || processingAI}
                      className={cn(
                        "text-sm transition-colors",
                        loading || processingAI 
                          ? "text-gray-400 cursor-not-allowed" 
                          : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      )}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Creating...
                        </span>
                      ) : (
                        "or create empty product →"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      
      <ProcessingModal />
    </div>
  );
};

export default NewProductPage;
